import { AlertTriangle, Building2, CalendarDays, ChevronRight, FileText, GitPullRequestArrow, Wallet } from 'lucide-react';
import {
  contractDaysLeft,
  contractOverdue,
  formatSedAmount,
  formatSedDate,
  letterOverdue,
  sedDocumentDueDate,
  sedDocumentStatusView,
  sedDocumentSubtitle,
  sedDocumentTitle,
  type SedDocument,
} from '@/lib/sed';
import { cn } from '@/lib/utils';
import { SedStatusChip } from './SedStatusChip';

export function SedDocCard({
  doc,
  active,
  onClick,
}: {
  doc: SedDocument;
  active: boolean;
  onClick: () => void;
}) {
  const status = sedDocumentStatusView(doc);
  const due = sedDocumentDueDate(doc);
  const overdue = doc.kind === 'contract' ? contractOverdue(doc) : letterOverdue(doc);
  const daysLeft = doc.kind === 'contract' ? contractDaysLeft(doc) : null;
  const subtitle = sedDocumentSubtitle(doc);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group w-full rounded-2xl border bg-card p-3 text-left shadow-sm transition-colors',
        active ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/20' : 'hover:border-primary/30 hover:bg-accent/30',
      )}
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <FileText className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1 truncate text-sm font-semibold leading-5">{sedDocumentTitle(doc)}</div>
            <SedStatusChip code={status.code} label={status.label} fromRoute={status.fromRoute} />
          </div>
          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
            {formatSedDate(doc.date) || 'Без даты'}
            {doc.kind === 'letter' && doc.directionLabel ? ` · ${doc.directionLabel}` : ''}
          </div>
        </div>
      </div>

      {subtitle && (
        <div className="mt-2 line-clamp-2 whitespace-pre-wrap break-words text-xs leading-5 text-foreground/85">
          {subtitle}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {doc.counterpartyName && (
          <span className="inline-flex min-w-0 items-center gap-1">
            <Building2 className="h-3 w-3 shrink-0" />
            <span className="max-w-[180px] truncate">{doc.counterpartyName}</span>
          </span>
        )}
        {doc.kind === 'contract' && doc.amount && (
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Wallet className="h-3 w-3 shrink-0" />
            {formatSedAmount(doc.amount)}
          </span>
        )}
        {due && (
          <span
            className={cn(
              'inline-flex items-center gap-1',
              overdue && 'font-medium text-red-600',
            )}
          >
            <CalendarDays className="h-3 w-3 shrink-0" />
            до {formatSedDate(due)}
            {overdue
              ? ' · просрочен'
              : daysLeft !== null && daysLeft >= 0 && daysLeft <= 7
                ? ` · ${daysLeft === 0 ? 'сегодня' : `${daysLeft} дн.`}`
                : ''}
          </span>
        )}
        {doc.route?.status === 'running' && (
          <span className="inline-flex items-center gap-1 text-sky-700">
            <GitPullRequestArrow className="h-3 w-3 shrink-0" />
            шаг {doc.route.currentStep}/{doc.route.stepsTotal}
          </span>
        )}
        {overdue && <AlertTriangle className="ml-auto h-3.5 w-3.5 text-red-500" />}
      </div>
    </button>
  );
}
